import { formatHourAsTime } from "@/utils/formatHourAsTime";
import React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { BookingModal } from "@/components/Scheduler/schedule/BookingModal";
import { GoogleEventDetailsModal } from "@/components/Calendar/schedule/GoogleEventDetailsModal";
import { BookingCard } from "./BookingCard";
import { BlockOutCard } from "./BlockOutCard";
import { TaskCard } from "./TaskCard";
import {
  type Booking,
  type BlockOut,
  type Task,
  useScheduleData,
  type UseScheduleDataReturn,
  isResourceAvailable,
  getMemberByFullName,
  isMemberWorkingAt,
  getBlockOutLanes,
  type BlockOutLane,
} from "@/components/Calendar/scheduleData";

type GoogleCalendarEvent = {
  id: string;
  summary: string;
  description?: string;
  date: string;
  time: string;
  duration: number;
  htmlLink?: string;
  resourceId?: string;
};

interface TimeSlotCellProps {
  dateString: string;
  hour: number;
  hourBookings: Booking[];
  hourBlockOuts: BlockOut[];
  hourTasks?: Task[];
  serviceColors: Record<string, string>;
  serviceColorsFromData?: Record<string, string>;
  serviceNameMap?: Record<string, string>;
  serviceColorMap?: Record<string, string>;
  onCreateBooking: (bookingData: any) => void;
  onCreateBookingWithDefaults?: (options: {
    date: string;
    time?: string;
    pharmacist?: string;
    serviceType?: string;
  }) => void;
  onEditBooking: (bookingId: string, bookingData: any) => void;
  onDeleteBooking: (bookingId: string) => void;
  onEditBlockOut?: (blockOutId: string, blockOut: BlockOut) => void;
  onEditTask?: (taskId: string, task: Task) => void;
  pharmacists: string[];
  rooms: string[];
  size?: "week" | "day";
  isDarkMode?: boolean;
  onBookingDrop?: (
    booking: Booking,
    newDate: string,
    newTime?: string,
    newPharmacist?: string,
    newRoom?: string,
  ) => void;
  onDurationChange?: (bookingId: string, newDuration: number) => void;
  onBlockOutDurationChange?: (blockOutId: string, newEndTime: string) => void;
  isNonWorkingHours?: boolean;
  hourGoogleEvents?: GoogleCalendarEvent[];
  weekGoogleEventsByDay?: Record<string, GoogleCalendarEvent[]>;
  // New props for week view with day grouping
  weekBookingsByDay?: Record<string, Booking[]>;
  weekBlockOutsByDay?: Record<string, BlockOut[]>;
  // Side-by-side lanes for overlapping block-outs, keyed by date then block-out id
  blockOutLanesByDay?: Record<string, Record<string, BlockOutLane>>;
  weekTasksByDay?: Record<string, Task[]>;
  weekDays?: Date[];
  nonWorkingHoursByDay?: Record<string, boolean>;
  // Props for pre-selecting based on column mode
  columnMode?:
    | "practitioner"
    | "day"
    | "services"
    | "room"
    | "day-practitioner";
  columnValue?: string;
  currentView?: string;
  notifiedBookingId?: string | null;
  notificationType?: "message" | "check";
  // Pulses this booking's own service-color border - used to point out one
  // specific appointment (e.g. clicked from the dashboard) among several.
  highlightedBookingId?: string | null;
  onStartConsultation?: (booking: Booking) => void;
}

interface GroupedBookingsAccordionProps {
  bookings: Booking[];
  hour: number;
  serviceColors: Record<string, string>;
  serviceNameMap?: Record<string, string>;
  serviceColorMap?: Record<string, string>;
  onEditBooking: (bookingId: string, bookingData: any) => void;
  onDeleteBooking: (bookingId: string) => void;
  onStartConsultation?: (booking: Booking) => void;
  pharmacists: string[];
  rooms: string[];
  isDarkMode: boolean;
  onDurationChange?: (bookingId: string, newDuration: number) => void;
  notifiedBookingId?: string | null;
  notificationType?: "message" | "check";
  highlightedBookingId?: string | null;
  cardSize?: "micro" | "small" | "medium" | "large" | "accordion";
}

const formatMinutes = (total: number) =>
  `${Math.floor(total / 60)
    .toString()
    .padStart(2, "0")}:${(total % 60).toString().padStart(2, "0")}`;

const parseMinutes = (time?: string) => {
  if (!time) return NaN;
  const [h, m] = time.split(":").map(Number);
  return isNaN(h) || isNaN(m) ? NaN : h * 60 + m;
};

// Header range spanning every item in the group; falls back to the 30-min slot
// when no item has a parseable time.
const getGroupTimeRange = (
  ranges: { start: number; end: number }[],
  hour: number,
) => {
  const valid = ranges.filter((r) => !isNaN(r.start) && !isNaN(r.end));
  if (valid.length > 0) {
    const minStart = Math.min(...valid.map((r) => r.start));
    const maxEnd = Math.max(...valid.map((r) => r.end));
    return `${formatMinutes(minStart)}–${formatMinutes(maxEnd)}`;
  }

  const slotStart = Math.round(hour * 60);
  return `${formatMinutes(slotStart)}–${formatMinutes(slotStart + 30)}`;
};

interface GroupAccordionProps {
  label: string;
  isDarkMode: boolean;
  children: React.ReactNode;
  // Lets booking groups paint above block-out groups when they overlap,
  // mirroring the z-20/z-10 split used by the individual (non-grouped) cards.
  zIndexClassName?: string;
}

function GroupAccordion({
  label,
  isDarkMode,
  children,
  zIndexClassName = "",
}: GroupAccordionProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    // flex-none (not flex-1): several accordions can share one cell, and
    // flex-1 would split the cell height equally and clip the open one
    <div
      className={`flex-none w-full p-3 min-h-[56px] transition-all duration-200 relative overflow-hidden rounded-md ${zIndexClassName} ${
        isDarkMode
          ? "bg-gray-700 border-gray-500"
          : "bg-gray-50 border-gray-500"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Accordion Header */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className={`w-full flex items-center justify-between px-3.5 py-2 text-left transition-colors duration-200 cursor-pointer ${
          isDarkMode
            ? "hover:bg-gray-700/50 text-gray-200"
            : "hover:bg-gray-50 text-gray-700"
        } ${isExpanded ? "border-b border-gray-200/80 dark:border-gray-700/80" : ""}`}
      >
        <span className="text-xs font-semibold leading-tight truncate">
          {label}
        </span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0 ml-1" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0 ml-1" />
        )}
      </button>

      {/* Accordion Content - grid-rows trick animates height without needing a fixed max-height */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          {/* Capped at ~3 cards; the rest scroll inside the accordion */}
          <div className="p-2 flex flex-col gap-2 w-full max-h-[260px] overflow-y-auto overscroll-contain">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

interface GroupedBlockOutsAccordionProps {
  blockOuts: BlockOut[];
  hour: number;
  isDarkMode: boolean;
  onEditBlockOut?: (blockOutId: string, blockOut: BlockOut) => void;
}

function GroupedBlockOutsAccordion({
  blockOuts,
  hour,
  isDarkMode,
  onEditBlockOut,
}: GroupedBlockOutsAccordionProps) {
  const timeRange = getGroupTimeRange(
    blockOuts.map((bo) => ({
      start: parseMinutes(bo.startTime),
      end: parseMinutes(bo.endTime),
    })),
    hour,
  );

  return (
    <GroupAccordion
      label={`${timeRange} • ${blockOuts.length} ${blockOuts.length === 1 ? "block-out" : "block-outs"}`}
      isDarkMode={isDarkMode}
      zIndexClassName="z-10"
    >
      {blockOuts.map((blockOut) => (
        <div key={blockOut.id} className="min-w-0 w-full">
          {/* Resize is off here: it absolutely positions multi-slot cards,
              which would overflow the accordion. */}
          <BlockOutCard
            blockOut={blockOut}
            size="accordion"
            isDarkMode={isDarkMode}
            onEdit={onEditBlockOut}
            enableResize={false}
          />
        </div>
      ))}
    </GroupAccordion>
  );
}

function GroupedBookingsAccordion({
  bookings,
  hour,
  serviceColors,
  serviceNameMap,
  serviceColorMap,
  onEditBooking,
  onDeleteBooking,
  onStartConsultation,
  pharmacists,
  rooms,
  isDarkMode,
  onDurationChange,
  notifiedBookingId,
  notificationType,
  highlightedBookingId,
  cardSize = "accordion",
}: GroupedBookingsAccordionProps) {
  const timeRange = getGroupTimeRange(
    bookings.map((b) => {
      const start = parseMinutes(b.time);
      return { start, end: start + (b.duration || 30) };
    }),
    hour,
  );

  return (
    <GroupAccordion
      label={`${timeRange} • ${bookings.length} ${bookings.length === 1 ? "appointment" : "appointments"}`}
      isDarkMode={isDarkMode}
      zIndexClassName="z-20"
    >
      {bookings.map((booking) => (
        <div key={booking.id} className="min-w-0 w-full">
          <BookingCard
            booking={booking}
            serviceColors={serviceColors}
            serviceNameMap={serviceNameMap}
            serviceColorMap={serviceColorMap}
            onEdit={onEditBooking}
            onDelete={onDeleteBooking}
            onStartConsultation={onStartConsultation}
            pharmacists={pharmacists}
            rooms={rooms}
            size={cardSize}
            isDarkMode={isDarkMode}
            enableResize={true}
            onDurationChange={onDurationChange}
            showNotification={notifiedBookingId === booking.id}
            notificationType={notificationType}
            pulseHighlight={highlightedBookingId === booking.id}
          />
        </div>
      ))}
    </GroupAccordion>
  );
}

// Offsets a block-out into its lane so overlapping block-outs sit side by side
const getLaneStyle = (lane?: BlockOutLane): React.CSSProperties | undefined =>
  lane && lane.lanes > 1
    ? {
        marginLeft: `${(lane.lane * 100) / lane.lanes}%`,
        width: `calc(${100 / lane.lanes}% - 2px)`,
      }
    : undefined;

const blockOutAffects = (bo: BlockOut, normP: string, pResId: string) => {
  if (!bo.affectedPharmacists || bo.affectedPharmacists.length === 0) {
    return true;
  }
  return bo.affectedPharmacists.some((p: string) => {
    const normAff = p.trim().toLowerCase();
    return normAff === normP || (!!pResId && normAff === pResId);
  });
};

export function TimeSlotCell({
  dateString,
  hour,
  hourBookings,
  hourBlockOuts,
  hourTasks = [],
  serviceColors,
  serviceColorsFromData,
  serviceNameMap = {},
  serviceColorMap = {},
  onCreateBooking,
  onCreateBookingWithDefaults,
  onEditBooking,
  onDeleteBooking,
  onEditBlockOut,
  onEditTask,
  onStartConsultation,
  pharmacists,
  rooms,
  size = "week",
  isDarkMode = false,
  onBookingDrop,
  onDurationChange,
  onBlockOutDurationChange,
  isNonWorkingHours = false,
  hourGoogleEvents = [],
  weekGoogleEventsByDay,
  weekBookingsByDay,
  weekBlockOutsByDay,
  blockOutLanesByDay,
  weekTasksByDay,
  weekDays,
  nonWorkingHoursByDay,
  columnMode,
  columnValue,
  currentView,
  notifiedBookingId,
  notificationType = "check",
  highlightedBookingId,
}: TimeSlotCellProps) {
  const s = useScheduleData();
  const { pharmacistResources: resources = [] } = (s || {}) as Partial<UseScheduleDataReturn>;

  const isDayMode =
    currentView === "day" ||
    size === "day" ||
    columnMode === "day" ||
    (weekDays && weekDays.length === 1);

  const [isDragOver, setIsDragOver] = React.useState(false);
  const [dragOverDayIndex, setDragOverDayIndex] = React.useState<number | null>(
    null,
  );
  const [selectedGoogleEvent, setSelectedGoogleEvent] =
    React.useState<any>(null);
  const [isGoogleEventModalOpen, setIsGoogleEventModalOpen] =
    React.useState(false);
  const formatTimeSlot = (timeSlot: number) => {
    const slotHour = Math.floor(timeSlot);
    const slotMinutes = timeSlot % 1 === 0 ? 0 : 30;

    return `${slotHour.toString().padStart(2, "0")}:${slotMinutes
      .toString()
      .padStart(2, "0")}`;
  };
  const initialTime = formatTimeSlot(hour);

  const isPastDate = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(`${dateStr}T00:00:00`);
    return date.getTime() < today.getTime();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const bookingData = e.dataTransfer.getData("application/json");
    if (bookingData && onBookingDrop) {
      const booking = JSON.parse(bookingData) as Booking;
      // Trigger drop with new date and time
      const newTime = formatTimeSlot(hour);
      const newPharmacist =
        (columnMode === "practitioner" || columnMode === "day-practitioner") &&
        columnValue &&
        columnValue !== "Unassigned"
          ? columnValue
          : undefined;
      onBookingDrop(booking, dateString, newTime, newPharmacist);
    }
  };

  const canClickSlot = !isNonWorkingHours && !isPastDate(dateString);

  if (size === "day") {
    return (
      <>
        <BookingModal
          trigger={
            <div
              className={`flex-1 p-3 min-h-[56px] transition-all duration-200 relative overflow-visible ${
                isNonWorkingHours || isPastDate(dateString)
                  ? isDarkMode
                    ? "bg-gray-800 cursor-not-allowed pointer-events-none"
                    : "bg-gray-200 cursor-not-allowed pointer-events-none"
                  : isDragOver
                    ? isDarkMode
                      ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                      : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                    : isDarkMode
                      ? "hover:bg-gray-700 cursor-pointer"
                      : "hover:bg-gray-50 cursor-pointer"
              }`}
              style={
                isNonWorkingHours || isPastDate(dateString)
                  ? {
                      backgroundImage: `repeating-linear-gradient(
                      45deg,
                      transparent,
                      transparent 10px,
                      ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 10px,
                      ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 20px
                    )`,
                    }
                  : undefined
              }
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div
                className="relative overflow-visible"
                style={{
                  display: hourBookings.length > 0 ? "block" : "block",
                }}
              >
                {hourBookings.length > 1 ? (
                  <GroupedBookingsAccordion
                    bookings={hourBookings}
                    hour={hour}
                    serviceColors={serviceColors}
                    serviceNameMap={serviceNameMap}
                    serviceColorMap={serviceColorMap}
                    onEditBooking={onEditBooking}
                    onDeleteBooking={onDeleteBooking}
                    onStartConsultation={onStartConsultation}
                    pharmacists={pharmacists}
                    rooms={rooms}
                    isDarkMode={isDarkMode}
                    onDurationChange={onDurationChange}
                    notifiedBookingId={notifiedBookingId}
                    notificationType={notificationType}
                    highlightedBookingId={highlightedBookingId}
                    cardSize="accordion"
                  />
                ) : (
                  hourBookings.map((booking) => (
                    <div key={booking.id} className="relative min-w-0 w-full">
                      <BookingCard
                        booking={booking}
                        serviceColors={serviceColors}
                        serviceNameMap={serviceNameMap}
                        serviceColorMap={serviceColorMap}
                        onEdit={onEditBooking}
                        onDelete={onDeleteBooking}
                        onStartConsultation={onStartConsultation}
                        pharmacists={pharmacists}
                        rooms={rooms}
                        size="large"
                        isDarkMode={isDarkMode}
                        enableResize={true}
                        onDurationChange={onDurationChange}
                        showNotification={notifiedBookingId === booking.id}
                        notificationType={notificationType}
                        pulseHighlight={highlightedBookingId === booking.id}
                      />
                    </div>
                  ))
                )}

                {hourGoogleEvents.length > 0 && (
                  <div
                    className="grid gap-2 mt-2"
                    style={{
                      gridTemplateColumns: `repeat(${hourGoogleEvents.length}, 1fr)`,
                    }}
                  >
                    {hourGoogleEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedGoogleEvent(event);
                          setIsGoogleEventModalOpen(true);
                        }}
                        className={`rounded-md border p-2 text-[11px] font-medium overflow-hidden truncate cursor-pointer transition-all hover:shadow-md ${
                          isDarkMode
                            ? "bg-blue-950/80 border-blue-700 text-white hover:bg-blue-900/90"
                            : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                                isDarkMode
                                  ? "bg-red-400 text-black"
                                  : "bg-red-600 text-white"
                              }`}
                              title="Google event"
                            >
                              G
                            </span>
                            <div className="truncate min-w-0">
                              {event.summary}
                            </div>
                          </div>
                        </div>

                        <div className="mt-1 text-[10px]">
                          {event.time} • {event.duration}m
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Block-out periods */}
                <div
                  style={{
                    display: hourBlockOuts.length === 1 ? "grid" : "block",
                    gridTemplateColumns:
                      hourBlockOuts.length === 1 ? "1fr" : undefined,
                    marginTop: hourBookings.length > 0 ? "8px" : "0",
                  }}
                >
                  {hourBlockOuts.length > 1 ? (
                    <GroupedBlockOutsAccordion
                      blockOuts={hourBlockOuts}
                      hour={hour}
                      isDarkMode={isDarkMode}
                      onEditBlockOut={onEditBlockOut}
                    />
                  ) : (
                    hourBlockOuts.map((blockOut) => (
                      <div key={blockOut.id} className="min-w-0">
                        <BlockOutCard
                          blockOut={blockOut}
                          size="large"
                          isDarkMode={isDarkMode}
                          onEdit={onEditBlockOut}
                          enableResize={true}
                          onDurationChange={onBlockOutDurationChange}
                        />
                      </div>
                    ))
                  )}
                </div>

                {/* Tasks */}
                <div
                  style={{
                    display: hourTasks.length > 0 ? "grid" : "block",
                    gridTemplateColumns:
                      hourTasks.length > 0
                        ? `repeat(${hourTasks.length}, 1fr)`
                        : undefined,
                    gap: hourTasks.length > 0 ? "8px" : undefined,
                    marginTop:
                      hourBookings.length > 0 || hourBlockOuts.length > 0
                        ? "8px"
                        : "0",
                  }}
                >
                  {hourTasks.map((task) => (
                    <div key={task.id} className="min-w-0">
                      <TaskCard
                        task={task}
                        size="large"
                        isDarkMode={isDarkMode}
                        onEdit={onEditTask}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          }
          onSave={onCreateBooking}
          pharmacists={pharmacists}
          rooms={rooms}
          initialDate={dateString}
          initialTime={initialTime}
          initialPharmacist={
            columnMode === "practitioner" ? columnValue : undefined
          }
          initialServiceType={
            columnMode === "services" ? (columnValue as any) : undefined
          }
          isDarkMode={isDarkMode}
        />
        <GoogleEventDetailsModal
          isDarkMode={isDarkMode}
          event={selectedGoogleEvent}
          isOpen={isGoogleEventModalOpen}
          onClose={() => setIsGoogleEventModalOpen(false)}
        />
      </>
    );
  }

  // Day-Practitioner mode (Practitioners nested under day columns)
  if (columnMode === "day-practitioner") {
    const dayDateString = dateString || columnValue;
    const allBookings =
      weekBookingsByDay?.[dayDateString] || hourBookings || [];
    const allBlockOuts =
      weekBlockOutsByDay?.[dayDateString] || hourBlockOuts || [];
    const allTasks = weekTasksByDay?.[dayDateString] || [];
    const allGoogleEvents = weekGoogleEventsByDay?.[dayDateString] || [];
    const displayList =
      pharmacists && pharmacists.length > 0 ? pharmacists : ["Unassigned"];

    return (
      <>
        <div
          className={`grid border-r min-h-14 transition-all duration-200 relative overflow-visible ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
          style={{ gridTemplateColumns: `repeat(${displayList.length}, 1fr)` }}
        >
          {displayList.map((pharmacistName, pIndex) => {
            const normP = (pharmacistName || "").trim().toLowerCase();
            const pResource = (resources || []).find(
              (r: any) =>
                (r.name && r.name.trim().toLowerCase() === normP) ||
                (r.id && String(r.id).trim().toLowerCase() === normP),
            );
            const pResId = (pResource?.id || "").trim().toLowerCase();

            // Filter bookings for this practitioner
            const dayBookings = allBookings.filter((b) => {
              const normB = (b.pharmacist || "").trim().toLowerCase();
              return normB === normP || (pResId && normB === pResId);
            });

            // Filter blockouts for this practitioner
            const dayBlockOuts = allBlockOuts.filter((bo) =>
              blockOutAffects(bo, normP, pResId),
            );

            const practitionerBlockOutLanes =
              dayBlockOuts.length > 0 && s?.getFilteredBlockOutsForDate
                ? getBlockOutLanes(
                    s
                      .getFilteredBlockOutsForDate(dayDateString)
                      .filter((bo) => blockOutAffects(bo, normP, pResId)),
                    dayDateString,
                  )
                : undefined;

            // Filter tasks for this practitioner
            const dayTasks = allTasks.filter((t) => {
              const normAssigned = (t.assignedTo || "").trim().toLowerCase();
              return (
                normAssigned === normP ||
                (pResId && normAssigned === pResId) ||
                !t.assignedTo ||
                t.assignedTo === "" ||
                t.assignedTo === "Unassigned"
              );
            });

            const dayGoogleEvents = allGoogleEvents.filter((event) => {
              const normEventResourceId = (event.resourceId || "")
                .trim()
                .toLowerCase();
              // No resourceId on the event means it can't be attributed to anyone - keep it visible.
              if (!normEventResourceId) return true;

              const pResEmail = (
                pResource?.userEmail ||
                pResource?.email ||
                ""
              )
                .trim()
                .toLowerCase();

              return (
                normEventResourceId === normP ||
                (pResId && normEventResourceId === pResId) ||
                (pResEmail && normEventResourceId === pResEmail)
              );
            });

            // Check practitioner availability for this specific day and time slot
            const timeString = formatTimeSlot(hour);
            const dayObj = new Date(`${dayDateString}T00:00:00`);
            const dayOfWeek = dayObj.getDay();

            let isWorking = true;
            if (pharmacistName && pharmacistName !== "Unassigned") {
              if (pResource) {
                isWorking = isResourceAvailable(
                  pResource,
                  dayOfWeek,
                  timeString,
                );
              } else {
                const member = getMemberByFullName(pharmacistName);
                if (member) {
                  isWorking = timeString
                    ? isMemberWorkingAt(member, dayOfWeek, timeString)
                    : true;
                }
              }
            }

            const isNonWorking = !isWorking;
            const isDayDragOver = dragOverDayIndex === pIndex;

            const handleDayDragOver = (e: React.DragEvent) => {
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(pIndex);
              e.dataTransfer.dropEffect = "move";
            };

            const handleDayDragLeave = (e: React.DragEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);
            };

            const handleDayDrop = (e: React.DragEvent) => {
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);

              const bookingData = e.dataTransfer.getData("application/json");
              if (bookingData && onBookingDrop) {
                const booking = JSON.parse(bookingData) as Booking;
                const newTime = formatTimeSlot(hour);
                const newPharm =
                  pharmacistName !== "Unassigned" ? pharmacistName : undefined;
                onBookingDrop(booking, dayDateString, newTime, newPharm);
              }
            };

            return (
              <div
                key={pIndex}
                className={`p-1.5 border-r last:border-r-0 transition-all duration-200 relative overflow-visible flex flex-col gap-1 ${
                  isNonWorking || isPastDate(dayDateString)
                    ? isDarkMode
                      ? "bg-gray-800 cursor-not-allowed border-gray-700"
                      : "bg-gray-200 cursor-not-allowed border-gray-300"
                    : isDayDragOver
                      ? isDarkMode
                        ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                        : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                      : isDarkMode
                        ? "border-gray-700 hover:bg-gray-700/50 cursor-pointer"
                        : "border-gray-300 hover:bg-gray-100 cursor-pointer"
                }`}
                style={
                  isNonWorking || isPastDate(dayDateString)
                    ? {
                        backgroundImage: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 12px
                      )`,
                      }
                    : undefined
                }
                title={
                  isPastDate(dayDateString)
                    ? `Cannot create booking on past dates`
                    : isNonWorking
                      ? `Not available - ${dayDateString}`
                      : `Click to add booking for ${pharmacistName} on ${dayDateString}`
                }
                onClick={
                  isNonWorking || isPastDate(dayDateString)
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        const timeString = formatTimeSlot(hour);
                        if (onCreateBookingWithDefaults) {
                          onCreateBookingWithDefaults({
                            date: dayDateString,
                            time: timeString,
                            pharmacist:
                              pharmacistName !== "Unassigned"
                                ? pharmacistName
                                : undefined,
                          });
                        } else {
                          onCreateBooking({
                            date: dayDateString,
                            time: timeString,
                          });
                        }
                      }
                }
                onDragOver={handleDayDragOver}
                onDragLeave={handleDayDragLeave}
                onDrop={handleDayDrop}
              >
                {/* Bookings */}
                {dayBookings.length > 1 && isDayMode ? (
                  <GroupedBookingsAccordion
                    bookings={dayBookings}
                    hour={hour}
                    serviceColors={serviceColors}
                    serviceNameMap={serviceNameMap}
                    serviceColorMap={serviceColorMap}
                    onEditBooking={onEditBooking}
                    onDeleteBooking={onDeleteBooking}
                    onStartConsultation={onStartConsultation}
                    pharmacists={pharmacists}
                    rooms={rooms}
                    isDarkMode={isDarkMode}
                    onDurationChange={onDurationChange}
                    notifiedBookingId={notifiedBookingId}
                    notificationType={notificationType}
                    highlightedBookingId={highlightedBookingId}
                    cardSize="accordion"
                  />
                ) : dayBookings.length > 0 ? (
                  <div
                    className="flex flex-row gap-0.5 w-full items-start"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayBookings.length}, 1fr)`,
                      gap: "2px",
                    }}
                  >
                    {dayBookings.map((booking) => (
                      <div key={booking.id} className="min-w-0 w-full">
                        <BookingCard
                          booking={booking}
                          serviceColors={serviceColors}
                          serviceNameMap={serviceNameMap}
                          serviceColorMap={serviceColorMap}
                          onEdit={onEditBooking}
                          onDelete={onDeleteBooking}
                          onStartConsultation={onStartConsultation}
                          pharmacists={pharmacists}
                          rooms={rooms}
                          size="micro"
                          isDarkMode={isDarkMode}
                          enableResize={true}
                          onDurationChange={onDurationChange}
                          showNotification={notifiedBookingId === booking.id}
                          notificationType={notificationType}
                          pulseHighlight={highlightedBookingId === booking.id}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Google events */}
                {dayGoogleEvents.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayGoogleEvents.length}, 1fr)`,
                      gap: "2px",
                      marginTop: dayBookings.length > 0 ? "4px" : "0",
                    }}
                  >
                    {dayGoogleEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedGoogleEvent(event);
                          setIsGoogleEventModalOpen(true);
                        }}
                        className={`rounded-md border p-1 text-[11px] font-medium overflow-hidden truncate cursor-pointer transition-all hover:shadow-md ${
                          isDarkMode
                            ? "bg-blue-950/80 border-blue-700 text-white hover:bg-blue-900/90"
                            : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                        }`}
                      >
                        <div className="truncate">{event.summary}</div>
                        <div className="mt-1 text-[10px]">
                          {event.time} • {event.duration}m
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Block-out periods */}
                {dayBlockOuts.length > 1 && isDayMode ? (
                  <GroupedBlockOutsAccordion
                    blockOuts={dayBlockOuts}
                    hour={hour}
                    isDarkMode={isDarkMode}
                    onEditBlockOut={onEditBlockOut}
                  />
                ) : dayBlockOuts.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={
                      practitionerBlockOutLanes
                        ? { marginTop: dayBookings.length > 0 ? "4px" : "0" }
                        : {
                            display: "grid",
                            gridTemplateColumns: `repeat(${dayBlockOuts.length}, 1fr)`,
                            gap: "2px",
                            marginTop: dayBookings.length > 0 ? "4px" : "0",
                          }
                    }
                  >
                    {dayBlockOuts.map((blockOut) => (
                      <div
                        key={blockOut.id}
                        className="min-w-0"
                        style={getLaneStyle(practitionerBlockOutLanes?.[blockOut.id])}
                      >
                        <BlockOutCard
                          blockOut={blockOut}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditBlockOut}
                          enableResize={true}
                          onDurationChange={onBlockOutDurationChange}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Tasks */}
                {dayTasks.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayTasks.length}, 1fr)`,
                      gap: "2px",
                      marginTop:
                        dayBookings.length > 0 || dayBlockOuts.length > 0
                          ? "4px"
                          : "0",
                    }}
                  >
                    {dayTasks.map((task) => (
                      <div key={task.id} className="min-w-0">
                        <TaskCard
                          task={task}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditTask}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <GoogleEventDetailsModal
          isDarkMode={isDarkMode}
          event={selectedGoogleEvent}
          isOpen={isGoogleEventModalOpen}
          onClose={() => setIsGoogleEventModalOpen(false)}
        />
      </>
    );
  }

  // Week view with day grouping
  if (size === "week" && weekBookingsByDay && weekDays) {
    const formatDateFromDate = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    const numDays = weekDays.length;

    return (
      <>
        <div
          className={`grid border-r min-h-14 transition-all duration-200 relative overflow-visible ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
          style={{ gridTemplateColumns: `repeat(${numDays}, 1fr)` }}
        >
          {weekDays.map((day, dayIndex) => {
            const dayDateString = formatDateFromDate(day);
            const dayBookings = weekBookingsByDay[dayDateString] || [];
            const dayBlockOuts = weekBlockOutsByDay?.[dayDateString] || [];
            const dayBlockOutLanes = blockOutLanesByDay?.[dayDateString];
            const dayTasks = weekTasksByDay?.[dayDateString] || [];
            const dayGoogleEvents =
              weekGoogleEventsByDay?.[dayDateString] || [];
            const isNonWorking = nonWorkingHoursByDay?.[dayDateString] || false;
            const isDayDragOver = dragOverDayIndex === dayIndex;

            const handleDayDragOver = (e: React.DragEvent) => {
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(dayIndex);
              e.dataTransfer.dropEffect = "move";
            };

            const handleDayDragLeave = (e: React.DragEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);
            };

            const handleDayDrop = (e: React.DragEvent) => {
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);

              const bookingData = e.dataTransfer.getData("application/json");
              if (bookingData && onBookingDrop) {
                const booking = JSON.parse(bookingData) as Booking;
                // Trigger drop with new date and time
                const newTime = formatTimeSlot(hour);
                onBookingDrop(booking, dayDateString, newTime);
              }
            };

            return (
              <div
                key={dayIndex}
                className={`p-1.5 border-r last:border-r-0 transition-all duration-200 relative overflow-visible flex flex-col gap-1 ${
                  isNonWorking || isPastDate(dayDateString)
                    ? isDarkMode
                      ? "bg-gray-800 cursor-not-allowed border-gray-700"
                      : "bg-gray-200 cursor-not-allowed border-gray-300"
                    : isDayDragOver
                      ? isDarkMode
                        ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                        : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                      : isDarkMode
                        ? "border-gray-700 hover:bg-gray-700/50 cursor-pointer"
                        : "border-gray-300 hover:bg-gray-100 cursor-pointer"
                }`}
                style={
                  isNonWorking || isPastDate(dayDateString)
                    ? {
                        backgroundImage: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 12px
                      )`,
                      }
                    : undefined
                }
                title={
                  isPastDate(dayDateString)
                    ? `Cannot create booking on past dates`
                    : isNonWorking
                      ? `Not available - ${dayDateString}`
                      : `Click to add booking for ${dayDateString}`
                }
                onClick={
                  isNonWorking || isPastDate(dayDateString)
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        const timeString = formatHourAsTime(hour);

                        // Use enhanced booking creation with pre-selection when available
                        if (onCreateBookingWithDefaults) {
                          const options: any = {
                            date: dayDateString,
                            time: timeString,
                          };

                          // Pre-select based on column mode
                          if (columnMode === "practitioner" && columnValue) {
                            options.pharmacist = columnValue;
                          } else if (columnMode === "services" && columnValue) {
                            options.serviceType = columnValue;
                          }

                          onCreateBookingWithDefaults(options);
                        } else {
                          // Fallback to legacy method
                          onCreateBooking({
                            date: dayDateString,
                            time: timeString,
                          });
                        }
                      }
                }
                onDragOver={handleDayDragOver}
                onDragLeave={handleDayDragLeave}
                onDrop={handleDayDrop}
              >
                {/* Show booking cards stacked horizontally like Google Calendar, or grouped in Day view */}
                {dayBookings.length > 1 && isDayMode ? (
                  <GroupedBookingsAccordion
                    bookings={dayBookings}
                    hour={hour}
                    serviceColors={serviceColors}
                    serviceNameMap={serviceNameMap}
                    serviceColorMap={serviceColorMap}
                    onEditBooking={onEditBooking}
                    onDeleteBooking={onDeleteBooking}
                    onStartConsultation={onStartConsultation}
                    pharmacists={pharmacists}
                    rooms={rooms}
                    isDarkMode={isDarkMode}
                    onDurationChange={onDurationChange}
                    notifiedBookingId={notifiedBookingId}
                    notificationType={notificationType}
                    highlightedBookingId={highlightedBookingId}
                    cardSize="accordion"
                  />
                ) : dayBookings.length > 0 ? (
                  <div
                    className="flex flex-row gap-0.5 w-full items-start"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayBookings.length}, 1fr)`,
                      gap: "2px",
                    }}
                  >
                    {dayBookings.map((booking) => (
                      <div key={booking.id} className="min-w-0 w-full">
                        <BookingCard
                          booking={booking}
                          serviceColors={serviceColors}
                          serviceNameMap={serviceNameMap}
                          serviceColorMap={serviceColorMap}
                          onEdit={onEditBooking}
                          onDelete={onDeleteBooking}
                          onStartConsultation={onStartConsultation}
                          pharmacists={pharmacists}
                          rooms={rooms}
                          size="micro"
                          isDarkMode={isDarkMode}
                          enableResize={true}
                          onDurationChange={onDurationChange}
                          showNotification={notifiedBookingId === booking.id}
                          notificationType={notificationType}
                          pulseHighlight={highlightedBookingId === booking.id}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                {dayGoogleEvents.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayGoogleEvents.length}, 1fr)`,
                      gap: "2px",
                      marginTop: dayBookings.length > 0 ? "4px" : "0",
                    }}
                  >
                    {dayGoogleEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedGoogleEvent(event);
                          setIsGoogleEventModalOpen(true);
                        }}
                        className={`rounded-md border p-1 text-[11px] font-medium overflow-hidden truncate cursor-pointer transition-all hover:shadow-md ${
                          isDarkMode
                            ? "bg-blue-950/80 border-blue-700 text-white hover:bg-blue-900/90"
                            : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                        }`}
                      >
                        <div className="truncate">{event.summary}</div>
                        <div className="mt-1 text-[10px]">
                          {event.time} • {event.duration}m
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Block-out periods with full cards, grouped in Day view */}
                {dayBlockOuts.length > 1 && isDayMode ? (
                  <GroupedBlockOutsAccordion
                    blockOuts={dayBlockOuts}
                    hour={hour}
                    isDarkMode={isDarkMode}
                    onEditBlockOut={onEditBlockOut}
                  />
                ) : dayBlockOuts.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={
                      dayBlockOutLanes
                        ? { marginTop: dayBookings.length > 0 ? "4px" : "0" }
                        : {
                            display: "grid",
                            gridTemplateColumns: `repeat(${dayBlockOuts.length}, 1fr)`,
                            gap: "2px",
                            marginTop: dayBookings.length > 0 ? "4px" : "0",
                          }
                    }
                  >
                    {dayBlockOuts.map((blockOut) => (
                      <div
                        key={blockOut.id}
                        className="min-w-0"
                        style={getLaneStyle(dayBlockOutLanes?.[blockOut.id])}
                      >
                        <BlockOutCard
                          blockOut={blockOut}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditBlockOut}
                          enableResize={true}
                          onDurationChange={onBlockOutDurationChange}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Tasks */}
                {dayTasks.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayTasks.length}, 1fr)`,
                      gap: "2px",
                      marginTop:
                        dayBookings.length > 0 || dayBlockOuts.length > 0
                          ? "4px"
                          : "0",
                    }}
                  >
                    {dayTasks.map((task) => (
                      <div key={task.id} className="min-w-0">
                        <TaskCard
                          task={task}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditTask}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <GoogleEventDetailsModal
          isDarkMode={isDarkMode}
          event={selectedGoogleEvent}
          isOpen={isGoogleEventModalOpen}
          onClose={() => setIsGoogleEventModalOpen(false)}
        />
      </>
    );
  }

  // Week view (default - no day grouping)
  return (
    <>
      <BookingModal
        trigger={
          <div
            className={`p-2 h-12 border-r transition-all duration-200 relative overflow-visible ${
              isNonWorkingHours
                ? isDarkMode
                  ? "bg-gray-800 cursor-not-allowed pointer-events-none border-gray-700"
                  : "bg-gray-200 cursor-not-allowed pointer-events-none border-gray-300"
                : isDragOver
                  ? isDarkMode
                    ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                    : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                  : isDarkMode
                    ? "border-gray-600 hover:bg-gray-700 cursor-pointer"
                    : "border-gray-200 hover:bg-gray-50 cursor-pointer"
            }`}
            style={
              isNonWorkingHours
                ? {
                    backgroundImage: `repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 8px,
                    ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 8px,
                    ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 16px
                  )`,
                  }
                : undefined
            }
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {hourBookings.length > 1 && isDayMode ? (
              <GroupedBookingsAccordion
                bookings={hourBookings}
                hour={hour}
                serviceColors={serviceColors}
                serviceNameMap={serviceNameMap}
                serviceColorMap={serviceColorMap}
                onEditBooking={onEditBooking}
                onDeleteBooking={onDeleteBooking}
                onStartConsultation={onStartConsultation}
                pharmacists={pharmacists}
                rooms={rooms}
                isDarkMode={isDarkMode}
                onDurationChange={onDurationChange}
                notifiedBookingId={notifiedBookingId}
                notificationType={notificationType}
                highlightedBookingId={highlightedBookingId}
                cardSize="accordion"
              />
            ) : hourBookings.length > 0 ? (
              <div
                style={{
                  display: hourBookings.length > 0 ? "grid" : "block",
                  gridTemplateColumns:
                    hourBookings.length > 0
                      ? `repeat(${hourBookings.length}, 1fr)`
                      : undefined,
                  gap: hourBookings.length > 0 ? "4px" : undefined,
                }}
              >
                {hourBookings.map((booking) => (
                  <div key={booking.id} className="relative min-w-0 w-full">
                    <BookingCard
                      booking={booking}
                      serviceColors={serviceColors}
                      serviceNameMap={serviceNameMap}
                      serviceColorMap={serviceColorMap}
                      onEdit={onEditBooking}
                      onDelete={onDeleteBooking}
                      onStartConsultation={onStartConsultation}
                      pharmacists={pharmacists}
                      rooms={rooms}
                      size="small"
                      isDarkMode={isDarkMode}
                      enableResize={true}
                      onDurationChange={onDurationChange}
                      showNotification={notifiedBookingId === booking.id}
                      notificationType={notificationType}
                      pulseHighlight={highlightedBookingId === booking.id}
                    />
                  </div>
                ))}
              </div>
            ) : null}

            {/* Block-out periods */}
            {hourBlockOuts.length > 1 && isDayMode ? (
              <div style={{ marginTop: hourBookings.length > 0 ? "4px" : "0" }}>
                <GroupedBlockOutsAccordion
                  blockOuts={hourBlockOuts}
                  hour={hour}
                  isDarkMode={isDarkMode}
                  onEditBlockOut={onEditBlockOut}
                />
              </div>
            ) : (
            <div
              style={{
                display: hourBlockOuts.length > 0 ? "grid" : "block",
                gridTemplateColumns:
                  hourBlockOuts.length > 0
                    ? `repeat(${hourBlockOuts.length}, 1fr)`
                    : undefined,
                gap: hourBlockOuts.length > 0 ? "4px" : undefined,
                marginTop: hourBookings.length > 0 ? "4px" : "0",
              }}
            >
              {hourBlockOuts.map((blockOut) => (
                <div key={blockOut.id} className="min-w-0">
                  <BlockOutCard
                    blockOut={blockOut}
                    size="small"
                    isDarkMode={isDarkMode}
                    onEdit={onEditBlockOut}
                    enableResize={true}
                    onDurationChange={onBlockOutDurationChange}
                  />
                </div>
              ))}
            </div>
            )}
          </div>
        }
        onSave={onCreateBooking}
        pharmacists={pharmacists}
        rooms={rooms}
        initialDate={dateString}
        initialTime={initialTime}
        initialPharmacist={
          columnMode === "practitioner" ? columnValue : undefined
        }
        initialServiceType={
          columnMode === "services" ? (columnValue as any) : undefined
        }
        isDarkMode={isDarkMode}
      />
      <GoogleEventDetailsModal
        isDarkMode={isDarkMode}
        event={selectedGoogleEvent}
        isOpen={isGoogleEventModalOpen}
        onClose={() => setIsGoogleEventModalOpen(false)}
      />
    </>
  );
}
